import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { KeyRound, Loader2, X } from "lucide-react";

export const ChangePasswordModal = ({ forced = false, onClose }) => {
  const { refresh } = useAuth();
  const [current, setCurrent] = useState("");
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (pw.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    if (pw !== confirm) { toast.error("Passwords don't match"); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: pw });
      toast.success("Password updated — use it next time you sign in");
      await refresh?.();
      onClose?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[95] bg-black/85 flex items-center justify-center p-4"
      onClick={forced ? undefined : onClose}>
      <div className="bg-pb-surface border border-pb-border p-6 w-full max-w-sm" onClick={(e) => e.stopPropagation()} data-testid="change-password-modal">
        <div className="flex items-center justify-between mb-1">
          <span className="font-display font-bold uppercase flex items-center gap-2">
            <KeyRound className="w-4 h-4 text-pb-cyan" /> {forced ? "Set your new password" : "Change password"}
          </span>
          {!forced && <button onClick={onClose} data-testid="change-password-close"><X className="w-4 h-4 text-zinc-500 hover:text-white" /></button>}
        </div>
        <p className="text-[11px] text-zinc-500 mb-4">
          {forced
            ? "You signed in with a temporary password. Choose your own new password to continue — at least 8 characters."
            : "Pick something at least 8 characters long that you don't use anywhere else."}
        </p>
        {!forced && (
          <input data-testid="current-password-input" type="password" value={current} onChange={(e) => setCurrent(e.target.value)}
            placeholder="Current password" autoComplete="current-password"
            className="w-full bg-pb-elevated border border-pb-border px-3 py-2.5 text-white text-sm focus:outline-none focus:border-pb-cyan mb-2" />
        )}
        <input data-testid="new-password-input" type="password" value={pw} onChange={(e) => setPw(e.target.value)}
          placeholder="New password (8+ characters)" autoComplete="new-password"
          className="w-full bg-pb-elevated border border-pb-border px-3 py-2.5 text-white text-sm focus:outline-none focus:border-pb-cyan mb-2" />
        <input data-testid="confirm-password-input" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
          placeholder="Repeat new password" autoComplete="new-password" onKeyDown={(e) => e.key === "Enter" && submit()}
          className="w-full bg-pb-elevated border border-pb-border px-3 py-2.5 text-white text-sm focus:outline-none focus:border-pb-cyan mb-4" />
        <button data-testid="change-password-submit" onClick={submit} disabled={busy}
          className="w-full flex items-center justify-center gap-2 bg-pb-cyan text-black font-black uppercase tracking-widest py-2.5 text-xs hover:bg-[#BEF264] disabled:opacity-50">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <KeyRound className="w-3.5 h-3.5" />}
          {forced ? "Save & continue" : "Update password"}
        </button>
      </div>
    </div>
  );
};
